import React from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import cx from 'classnames';

import {
    IconEdit,
    IconCloseOutline,
    Text,
    Button,
    Input,
    Spacer,
} from '@zillow/constellation';

import api from 'app/shared/utils/api';
import UserManagementActions from 'app/shared/actions/UserManagementActions';
import { RMWContext } from 'app/shared/providers/RMWContext';

const TabList = {
    users: 'users',
    pending: 'pending',
    activity: 'activity',
    unrecognized: 'unrecognized-users',
};

const UserManagementNav = ({
    company,
    activeTab,
    history,
    dispatch,
}) => {
    const { paths } = React.useContext(RMWContext);
    const [isEditing, setIsEditing] = React.useState(false);
    const [companyName, setCompanyName] = React.useState('');
    const [editError, setEditError] = React.useState(null);

    const handleAddUser = () => {
        window.router.transitionTo(`${paths.userManagementPage}/${paths.userManagementAddUser}`);
    };

    const loadTab = (tab) => {
        const baseDetailsUrl = paths.userManagementPage;
        const url = `${baseDetailsUrl}/${tab}`;
        return history.push(url);
    };

    const handleEdit = () => {
        setEditError(null);
        setCompanyName(company.name);
        setIsEditing(true);
    };

    const handleSaveName = () => {
        dispatch(api.users.company.updateCompanyName(companyName))
            .then((response) => {
                if (!response.success) {
                    if (response.error && response.error.message) {
                        throw new Error(response.error.message);
                    }
                    throw new Error();
                }
                dispatch(UserManagementActions.setCompanyName(response.response));
                setIsEditing(false);
            })
            .catch((e) => {
                if (e && e.message) {
                    setEditError(e.message);
                } else {
                    setEditError('Failed to update Company Name, please try again');
                }
            });
    };

    const renderEditName = () => (
        <div className="edit-company-name-wrapper">
            <Input
                type="text"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                onKeyDown={e => e.keyCode === 13 && handleSaveName()}
                onBlur={handleSaveName}
                error={!!editError}
            />
            <IconCloseOutline className="close-edit-company-name-icon" onClick={() => setIsEditing(false)} />
        </div>
    );

    const renderHeading = () => company.name && (
        <>
            <Text fontType="h6">{company.name}</Text>
            <div className="edit-icon-wrapper" onClick={handleEdit} role="button" onKeyDown={handleEdit} tabIndex={0}>
                <IconEdit size="xs" />
            </div>
        </>
    );

    return (
        <div>
            <Spacer marginY="xs">
                {isEditing ? renderEditName() : renderHeading()}
            </Spacer>
            <ul className="UsersPage-SubNav-list">
                <li
                    className={cx('TabMenuItem-links', {
                        'TabMenuItem-active': activeTab === TabList.users,
                    })}
                    onClick={() => loadTab(paths.userManagementUsers)}
                >
                    <Text
                        className={cx({ 'Utils-primary': activeTab === TabList.users })}
                    >
                        Active Users
                        {company.userCount > 0 && (
                            <span className="UsersPage-tab-count">
                                {company.userCount}
                            </span>
                        )}
                    </Text>
                </li>
                <li
                    className={cx('TabMenuItem-links', {
                        'TabMenuItem-active': activeTab === TabList.unrecognized,
                    })}
                    onClick={() => loadTab(paths.userManagementUnrecognizedUsers)}
                >
                    <Text
                        className={cx({ 'Utils-primary': activeTab === TabList.unrecognized })}
                    >
                        Unrecognized Users
                    </Text>
                </li>
                <li
                    className={cx('TabMenuItem-links', {
                        'TabMenuItem-active': activeTab === TabList.pending,
                    })}
                    onClick={() => loadTab(paths.userManagementPending)}
                >
                    <Text
                        className={cx({ 'Utils-primary': activeTab === TabList.pending })}
                    >
                        Pending Users
                        {company.pendingUserCount > 0 && (
                            <span className="UsersPage-tab-count">
                                {company.pendingUserCount}
                            </span>
                        )}
                    </Text>
                </li>
                <li
                    className={cx('TabMenuItem-links', {
                        'TabMenuItem-active': activeTab === TabList.activity,
                    })}
                    onClick={() => loadTab(paths.userManagementActivity)}
                >
                    <Text
                        className={cx({ 'Utils-primary': activeTab === TabList.activity })}
                    >
                        Activity History
                    </Text>
                </li>
                <li
                    className="TabMenuItem-links TabMenuItem-Right"
                >
                    <Button
                        buttonType="primary"
                        fluid={true}
                        onClick={handleAddUser}
                    >
                        Add a new user
                    </Button>
                </li>
            </ul>
        </div>
    );
};

export default connect()(withRouter(UserManagementNav));
